import { useEffect, useMemo, useState } from 'react';
import { Building2, Power, RefreshCw, Save, Shield, Trash2, Upload, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LegacyOrganization } from '../types/legacy';
import {
  createOrganization,
  getCurrentLegacyUserProfile,
  getOrganizationById,
  listOrganizations,
  updateOrganization,
} from '../services/organizationProfileService';
import {
  createRegisteredAdminUser,
  CreateRegisteredAdminUserPayload,
  deleteRegisteredUser,
  listRegisteredUsers,
  RegisteredUserItem,
  updateRegisteredUserStatus,
} from '../services/registeredUsersService';
import { uploadOrganizationLogo } from '../services/uploadService';
import { resolveOrganizationLogoCandidates } from '../utils/imageUtils';
import { getExperiences } from '../services/legacyApiService';

const emptyOrganization: LegacyOrganization = {
  id: 0,
  name: '',
  email: '',
  website: '',
  logo: '',
  guarded: false,
};

function parseOrganizationIdFromUnknown(source: unknown): number | null {
  if (!source || typeof source !== 'object') return null;
  const record = source as Record<string, unknown>;

  const candidates = [
    record.id_organization,
    record.idOrganization,
    record.organizationId,
    (record.organization as Record<string, unknown> | undefined)?.id,
    (record.user as Record<string, unknown> | undefined)?.id_organization,
    (record.user as Record<string, unknown> | undefined)?.idOrganization,
    (record.user as Record<string, unknown> | undefined)?.organizationId,
    ((record.user as Record<string, unknown> | undefined)?.organization as Record<string, unknown> | undefined)?.id,
    (record.data as Record<string, unknown> | undefined)?.id_organization,
    (record.data as Record<string, unknown> | undefined)?.idOrganization,
    (record.data as Record<string, unknown> | undefined)?.organizationId,
    ((record.data as Record<string, unknown> | undefined)?.organization as Record<string, unknown> | undefined)?.id,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate || 0);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
}

function pickMostFrequentOrgId(values: number[]): number | null {
  if (!Array.isArray(values) || values.length === 0) return null;
  const map = new Map<number, number>();
  values.forEach((value) => {
    const parsed = Number(value || 0);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    map.set(parsed, (map.get(parsed) || 0) + 1);
  });
  let bestId: number | null = null;
  let bestCount = 0;
  map.forEach((count, id) => {
    if (count > bestCount) {
      bestCount = count;
      bestId = id;
    }
  });
  return bestId;
}

const ProfileOrganization = () => {
  const { isSuperAdmin, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [organization, setOrganization] = useState<LegacyOrganization>(emptyOrganization);
  const [logoPreviewCandidates, setLogoPreviewCandidates] = useState<string[]>([]);
  const [currentUserOrgId, setCurrentUserOrgId] = useState<number | null>(null);
  const [availableOrganizations, setAvailableOrganizations] = useState<LegacyOrganization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [admins, setAdmins] = useState<RegisteredUserItem[]>([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [createAdminModalOpen, setCreateAdminModalOpen] = useState(false);
  const [createOrgModalOpen, setCreateOrgModalOpen] = useState(false);
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [createNewOrganization, setCreateNewOrganization] = useState(false);
  const [deletingAdminId, setDeletingAdminId] = useState<string | null>(null);
  const [statusAdminId, setStatusAdminId] = useState<string | null>(null);
  const [adminForm, setAdminForm] = useState<CreateRegisteredAdminUserPayload>({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    temporaryPassword: '',
    organizationId: 0,
    organizationName: '',
    createOrganization: false,
    note: '',
  });
  const [newOrganizationForm, setNewOrganizationForm] = useState({
    name: '',
    email: '',
    website: '',
    logo: '',
    guarded: false,
  });

  const activeOrganizationId = selectedOrgId || currentUserOrgId;
  const logoPreview = logoPreviewCandidates[0] || '';
  const canManageAdmins = isSuperAdmin;

  const load = async (forcedOrgId?: number | null) => {
    try {
      setLoading(true);
      setError(null);

      const orgIdFromClaims = parseOrganizationIdFromUnknown(user);

      let currentUser: Record<string, unknown> | null = null;
      try {
        currentUser = await getCurrentLegacyUserProfile();
      } catch (profileError) {
        console.warn('[ProfileOrganization] fallback su claims/esperienze: /api/user non disponibile', profileError);
      }
      const parsedCurrentOrgId = parseOrganizationIdFromUnknown(currentUser) || orgIdFromClaims;
      setCurrentUserOrgId(parsedCurrentOrgId);

      let organizations: LegacyOrganization[] = [];
      if (isSuperAdmin) {
        organizations = await listOrganizations();
        setAvailableOrganizations(organizations);
      } else {
        setAvailableOrganizations([]);
      }

      let inferredOrgIdFromExperiences: number | null = null;
      if (!parsedCurrentOrgId) {
        try {
          const experiences = await getExperiences();
          const organizationIds = experiences
            .map((item) => Number(item.idOrganization || 0))
            .filter((id) => Number.isFinite(id) && id > 0);
          inferredOrgIdFromExperiences = pickMostFrequentOrgId(organizationIds);
        } catch (experienceError) {
          console.warn('[ProfileOrganization] impossibile inferire org da esperienze', experienceError);
        }
      }

      let inferredGloociStaffOrgId: number | null = null;
      if (!parsedCurrentOrgId && !inferredOrgIdFromExperiences) {
        try {
          const fallbackOrganizations = organizations.length > 0 ? organizations : await listOrganizations();
          const byLegacyName = fallbackOrganizations.find((item) =>
            String(item.name || '').trim().toLowerCase() === 'glooci staff');
          if (byLegacyName?.id) {
            inferredGloociStaffOrgId = Number(byLegacyName.id);
          }
        } catch (organizationError) {
          console.warn('[ProfileOrganization] impossibile leggere fallback organizzazioni', organizationError);
        }
      }

      const fallbackOrgId = parsedCurrentOrgId
        || inferredOrgIdFromExperiences
        || inferredGloociStaffOrgId
        || (organizations[0]?.id ? Number(organizations[0].id) : null);
      const targetOrgId = forcedOrgId || selectedOrgId || fallbackOrgId;

      if (!targetOrgId) {
        setOrganization(emptyOrganization);
        setLogoPreviewCandidates([]);
        setError('Nessuna organizzazione associata all\'utente corrente. Verifica mapping utente → organizzazione.');
        return;
      }

      const detail = await getOrganizationById(targetOrgId);
      setOrganization({
        id: Number(detail.id || targetOrgId),
        name: String(detail.name || ''),
        email: String(detail.email || ''),
        website: String(detail.website || ''),
        logo: String(detail.logo || ''),
        guarded: Boolean(detail.guarded),
      });
      setSelectedOrgId(Number(detail.id || targetOrgId));
      setLogoPreviewCandidates(resolveOrganizationLogoCandidates(detail.logo));
      if (canManageAdmins) {
        await loadAdmins(Number(detail.id || targetOrgId));
      } else {
        setAdmins([]);
      }
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : 'Errore caricamento profilo organizzazione.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const loadAdmins = async (organizationId?: number | null) => {
    if (!canManageAdmins) {
      setAdmins([]);
      return;
    }
    try {
      setLoadingAdmins(true);
      const normalizedOrgId = Number(organizationId || 0);
      const result = await listRegisteredUsers({
        category: 'amministratore',
        page: 1,
        pageSize: 300,
        organizationId: normalizedOrgId > 0 ? normalizedOrgId : 'all',
      });
      setAdmins(Array.isArray(result.users) ? result.users : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAdmins(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const onSelectOrganization = async (value: string) => {
    const parsed = Number(value || 0) || null;
    setSelectedOrgId(parsed);
    await load(parsed);
  };

  const openCreateOrganizationModal = () => {
    setNewOrganizationForm({
      name: '',
      email: '',
      website: '',
      logo: '',
      guarded: false,
    });
    setCreateOrgModalOpen(true);
  };

  const onCreateOrganization = async () => {
    const name = String(newOrganizationForm.name || '').trim();
    if (!name) {
      setError('Il nome della nuova organizzazione e obbligatorio.');
      return;
    }
    try {
      setCreatingOrg(true);
      setError(null);
      const created = await createOrganization({
        name,
        email: String(newOrganizationForm.email || '').trim(),
        website: String(newOrganizationForm.website || '').trim(),
        logo: String(newOrganizationForm.logo || '').trim(),
        guarded: Boolean(newOrganizationForm.guarded),
      });
      setCreateOrgModalOpen(false);
      setSuccess('Nuova organizzazione creata con successo.');
      setTimeout(() => setSuccess(null), 2500);
      await load(Number(created?.id || 0) || null);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Creazione organizzazione non riuscita.');
    } finally {
      setCreatingOrg(false);
    }
  };

  const openCreateAdminModal = () => {
    if (!canManageAdmins) return;
    const targetOrganizationId = Number(activeOrganizationId || 0);
    const targetOrganizationName = String(organization.name || '');
    setAdminForm({
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      temporaryPassword: '',
      organizationId: targetOrganizationId > 0 ? targetOrganizationId : 0,
      organizationName: targetOrganizationName,
      createOrganization: false,
      note: '',
    });
    setCreateNewOrganization(false);
    setCreateAdminModalOpen(true);
  };

  const onCreateAdmin = async () => {
    if (!canManageAdmins) return;
    const payload: CreateRegisteredAdminUserPayload = {
      username: String(adminForm.username || '').trim(),
      email: String(adminForm.email || '').trim().toLowerCase(),
      firstName: String(adminForm.firstName || '').trim(),
      lastName: String(adminForm.lastName || '').trim(),
      temporaryPassword: String(adminForm.temporaryPassword || '').trim(),
      organizationId: Number(adminForm.organizationId || 0),
      organizationName: String(adminForm.organizationName || '').trim(),
      createOrganization: Boolean(adminForm.createOrganization),
      note: String(adminForm.note || '').trim(),
    };
    if (!payload.username || !payload.email || !payload.firstName || !payload.lastName || !payload.temporaryPassword) {
      setError('Compila tutti i campi obbligatori per creare l\'admin.');
      return;
    }
    if (payload.createOrganization) {
      if (!payload.organizationName) {
        setError('Inserisci il nome della nuova organizzazione.');
        return;
      }
      payload.organizationId = undefined;
    } else if (!Number.isFinite(Number(payload.organizationId || 0)) || Number(payload.organizationId || 0) <= 0) {
      setError('Seleziona un\'organizzazione valida.');
      return;
    }
    try {
      setCreatingAdmin(true);
      setError(null);
      await createRegisteredAdminUser(payload);
      setCreateAdminModalOpen(false);
      setSuccess('Admin creato e associato correttamente.');
      setTimeout(() => setSuccess(null), 2500);
      await load();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Creazione admin non riuscita.');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const onToggleAdminStatus = async (item: RegisteredUserItem, enabled: boolean) => {
    if (!canManageAdmins) return;
    try {
      setStatusAdminId(item.id);
      await updateRegisteredUserStatus(item.id, enabled);
      setSuccess(enabled ? 'Admin attivato.' : 'Admin disattivato.');
      setTimeout(() => setSuccess(null), 2000);
      await loadAdmins(activeOrganizationId);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Aggiornamento admin non riuscito.');
    } finally {
      setStatusAdminId(null);
    }
  };

  const onDeleteAdmin = async (item: RegisteredUserItem) => {
    if (!canManageAdmins) return;
    const confirmed = window.confirm(`Confermi eliminazione admin "${item.username}"?`);
    if (!confirmed) return;
    try {
      setDeletingAdminId(item.id);
      await deleteRegisteredUser(item.id);
      setSuccess('Admin eliminato.');
      setTimeout(() => setSuccess(null), 2000);
      await loadAdmins(activeOrganizationId);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Eliminazione admin non riuscita.');
    } finally {
      setDeletingAdminId(null);
    }
  };

  const onSave = async () => {
    if (!organization.id) {
      setError('Organizzazione non valida.');
      return;
    }
    if (!String(organization.name || '').trim()) {
      setError('Il nome organizzazione è obbligatorio.');
      return;
    }
    if (!String(organization.email || '').trim()) {
      setError('L\'email organizzazione è obbligatoria.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const updated = await updateOrganization(organization);
      setOrganization({
        id: Number(updated.id || organization.id),
        name: String(updated.name || ''),
        email: String(updated.email || ''),
        website: String(updated.website || ''),
        logo: String(updated.logo || ''),
        guarded: Boolean(updated.guarded),
      });
      setLogoPreviewCandidates(resolveOrganizationLogoCandidates(updated.logo));
      setSuccess('Profilo organizzazione aggiornato.');
      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Salvataggio non riuscito.');
    } finally {
      setSaving(false);
    }
  };

  const onLogoChange = async (file?: File | null) => {
    if (!file || !organization.id) return;
    try {
      setUploadingLogo(true);
      setError(null);
      setSuccess(null);
      const uploaded = await uploadOrganizationLogo(file, organization.id);
      const payloadToSave: LegacyOrganization = {
        ...organization,
        logo: uploaded.storedValue,
      };
      setOrganization(payloadToSave);
      setLogoPreviewCandidates([
        uploaded.downloadUrl,
        ...resolveOrganizationLogoCandidates(uploaded.storedValue),
      ]);
      const saved = await updateOrganization(payloadToSave);
      setOrganization({
        id: Number(saved.id || payloadToSave.id),
        name: String(saved.name || ''),
        email: String(saved.email || ''),
        website: String(saved.website || ''),
        logo: String(saved.logo || ''),
        guarded: Boolean(saved.guarded),
      });
      setSuccess('Logo caricato e salvato.');
      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Upload logo non riuscito.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const organizationSelectorVisible = useMemo(
    () => isSuperAdmin && availableOrganizations.length > 0,
    [isSuperAdmin, availableOrganizations.length],
  );

  if (loading) {
    return (
      <div className="card p-6 text-text-secondary">
        Caricamento profilo organizzazione...
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary flex items-center gap-2">
            <Building2 size={28} className="text-primary" />
            Organizzazioni e amministratori
          </h1>
          <p className="text-text-secondary mt-1">
            Gestisci organizzazioni, associazioni e amministratori in un unico punto.
          </p>
        </div>
        <button
          type="button"
          className="btn-outline inline-flex items-center gap-2"
          onClick={() => void load(activeOrganizationId)}
          disabled={loading}
        >
          <RefreshCw size={15} />
          Aggiorna
        </button>
        {isSuperAdmin && (
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            onClick={openCreateOrganizationModal}
            disabled={creatingOrg}
          >
            <Building2 size={15} />
            Nuova organizzazione
          </button>
        )}
      </div>

      {error && <div className="rounded-lg border border-error/30 bg-error/10 p-4 text-error">{error}</div>}
      {success && <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-700">{success}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_1fr] gap-6">
        <div className="card p-5 space-y-4">
          {organizationSelectorVisible && (
            <label className="block">
              <span className="text-sm font-medium text-text-secondary block mb-1.5">
                Organizzazione (superadmin)
              </span>
              <select
                className="w-full rounded-lg border border-border px-3 py-2"
                value={organization.id || ''}
                onChange={(e) => void onSelectOrganization(e.target.value)}
              >
                {availableOrganizations.map((item) => (
                  <option key={item.id} value={item.id}>{item.name || `Org ${item.id}`}</option>
                ))}
              </select>
            </label>
          )}

          <label className="block">
            <span className="text-sm font-medium text-text-secondary block mb-1.5">Nome organizzazione</span>
            <input
              className="w-full rounded-lg border border-border px-3 py-2"
              value={String(organization.name || '')}
              onChange={(e) => setOrganization((prev) => ({ ...prev, name: e.target.value }))}
              maxLength={50}
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-text-secondary block mb-1.5">Email organizzazione</span>
            <input
              className="w-full rounded-lg border border-border px-3 py-2"
              value={String(organization.email || '')}
              onChange={(e) => setOrganization((prev) => ({ ...prev, email: e.target.value }))}
              maxLength={255}
              type="email"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-text-secondary block mb-1.5">Sito web</span>
            <input
              className="w-full rounded-lg border border-border px-3 py-2"
              value={String(organization.website || '')}
              onChange={(e) => setOrganization((prev) => ({ ...prev, website: e.target.value }))}
              maxLength={128}
              placeholder="https://..."
            />
          </label>

          <div className="pt-2">
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-2"
              onClick={() => void onSave()}
              disabled={saving || uploadingLogo}
            >
              <Save size={15} />
              {saving ? 'Salvataggio...' : 'Salva profilo'}
            </button>
          </div>
        </div>

        <div className="card p-5 space-y-4">
          <p className="text-sm font-semibold text-text-primary">Logo organizzazione</p>
          <div className="rounded-xl border border-border bg-gray-50 p-4 flex items-center justify-center min-h-[220px]">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Logo organizzazione"
                className="max-h-40 w-auto object-contain"
                onError={() => {
                  setLogoPreviewCandidates((prev) => prev.slice(1));
                }}
              />
            ) : (
              <span className="text-sm text-text-secondary">Nessun logo caricato</span>
            )}
          </div>

          <label className="btn-outline inline-flex items-center justify-center gap-2 cursor-pointer">
            <Upload size={15} />
            {uploadingLogo ? 'Upload...' : 'Carica logo'}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => void onLogoChange(e.target.files?.[0] || null)}
              disabled={uploadingLogo || saving}
            />
          </label>

          <div className="rounded-lg border border-border bg-gray-50 p-3 text-xs text-text-secondary space-y-1">
            <p><span className="font-semibold text-text-primary">ID:</span> {organization.id || 'n/d'}</p>
            <p><span className="font-semibold text-text-primary">Guarded:</span> {organization.guarded ? 'si' : 'no'}</p>
            <p><span className="font-semibold text-text-primary">Storage key:</span> {organization.logo || 'n/d'}</p>
          </div>

          {!isSuperAdmin && (
            <p className="text-xs text-text-secondary inline-flex items-center gap-1">
              <Shield size={12} className="text-primary" />
              Modifica consentita solo per la tua organizzazione.
            </p>
          )}
        </div>
      </div>

      {canManageAdmins && (
        <div className="card p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-text-primary">Amministratori associati</h2>
            <div className="flex items-center gap-2">
              <button type="button" className="btn-outline" onClick={() => void loadAdmins(activeOrganizationId)} disabled={loadingAdmins}>
                {loadingAdmins ? 'Aggiorno...' : 'Aggiorna elenco'}
              </button>
              <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={openCreateAdminModal}>
                <UserPlus size={15} /> Nuovo admin
              </button>
            </div>
          </div>
          <p className="text-sm text-text-secondary">
            Da qui puoi creare admin, associarli all&apos;organizzazione corrente e gestire stato/eliminazione.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-gray-50 text-text-secondary">
                <tr>
                  <th className="text-left px-3 py-2">Utente</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Organizzazione</th>
                  <th className="text-left px-3 py-2">Stato</th>
                  <th className="text-right px-3 py-2">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loadingAdmins ? (
                  <tr><td colSpan={5} className="px-3 py-4 text-text-secondary">Caricamento admin...</td></tr>
                ) : admins.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-4 text-text-secondary">Nessun admin associato.</td></tr>
                ) : (
                  admins.map((item) => (
                    <tr key={item.id}>
                      <td className="px-3 py-3">{item.username}</td>
                      <td className="px-3 py-3">{item.email || 'n/d'}</td>
                      <td className="px-3 py-3">{item.organizationName || 'n/d'}</td>
                      <td className="px-3 py-3">{item.enabled ? 'Attivo' : 'Disattivo'}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            className="btn-outline text-xs inline-flex items-center gap-1"
                            onClick={() => void onToggleAdminStatus(item, !item.enabled)}
                            disabled={statusAdminId === item.id}
                          >
                            <Power size={12} />
                            {item.enabled ? 'Disattiva' : 'Attiva'}
                          </button>
                          <button
                            type="button"
                            className="btn-outline text-xs inline-flex items-center gap-1"
                            onClick={() => void onDeleteAdmin(item)}
                            disabled={deletingAdminId === item.id}
                          >
                            <Trash2 size={12} />
                            Elimina
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {canManageAdmins && createAdminModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-border">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <h3 className="text-lg font-semibold text-text-primary">Crea e associa admin</h3>
              <button type="button" className="btn-outline text-xs" onClick={() => setCreateAdminModalOpen(false)} disabled={creatingAdmin}>Chiudi</button>
            </div>
            <div className="space-y-4 px-6 py-5">
              <div className="grid gap-3 md:grid-cols-2">
                <input className="w-full rounded-xl border border-border bg-white px-3 py-2" placeholder="Username *" value={adminForm.username} onChange={(e) => setAdminForm((prev) => ({ ...prev, username: e.target.value }))} disabled={creatingAdmin} />
                <input type="email" className="w-full rounded-xl border border-border bg-white px-3 py-2" placeholder="Email *" value={adminForm.email} onChange={(e) => setAdminForm((prev) => ({ ...prev, email: e.target.value }))} disabled={creatingAdmin} />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <input className="w-full rounded-xl border border-border bg-white px-3 py-2" placeholder="Nome *" value={adminForm.firstName} onChange={(e) => setAdminForm((prev) => ({ ...prev, firstName: e.target.value }))} disabled={creatingAdmin} />
                <input className="w-full rounded-xl border border-border bg-white px-3 py-2" placeholder="Cognome *" value={adminForm.lastName} onChange={(e) => setAdminForm((prev) => ({ ...prev, lastName: e.target.value }))} disabled={creatingAdmin} />
              </div>
              <input type="password" className="w-full rounded-xl border border-border bg-white px-3 py-2" placeholder="Password temporanea *" value={adminForm.temporaryPassword} onChange={(e) => setAdminForm((prev) => ({ ...prev, temporaryPassword: e.target.value }))} disabled={creatingAdmin} />
              {!createNewOrganization ? (
                <select
                  className="w-full rounded-xl border border-border bg-white px-3 py-2"
                  value={adminForm.organizationId || 0}
                  onChange={(e) => {
                    const selectedId = Number(e.target.value || 0);
                    const selectedOrganization = availableOrganizations.find((org) => Number(org?.id || 0) === selectedId);
                    setAdminForm((prev) => ({
                      ...prev,
                      organizationId: selectedId,
                      organizationName: String(selectedOrganization?.name || ''),
                      createOrganization: false,
                    }));
                  }}
                  disabled={creatingAdmin}
                >
                  <option value={0}>Seleziona organizzazione *</option>
                  {availableOrganizations.map((org) => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              ) : (
                <input className="w-full rounded-xl border border-border bg-white px-3 py-2" placeholder="Nome nuova organizzazione *" value={adminForm.organizationName || ''} onChange={(e) => setAdminForm((prev) => ({ ...prev, organizationId: undefined, organizationName: e.target.value, createOrganization: true }))} disabled={creatingAdmin} />
              )}
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                onClick={() => {
                  setCreateNewOrganization((prev) => !prev);
                  setAdminForm((prev) => ({
                    ...prev,
                    organizationId: undefined,
                    organizationName: '',
                    createOrganization: !createNewOrganization,
                  }));
                }}
                disabled={creatingAdmin}
              >
                {createNewOrganization ? 'Usa organizzazione esistente' : 'Organizzazione non in lista? Creane una nuova'}
              </button>
              <textarea className="w-full rounded-xl border border-border bg-white px-3 py-2" rows={3} placeholder="Nota interna (opzionale)" value={adminForm.note || ''} onChange={(e) => setAdminForm((prev) => ({ ...prev, note: e.target.value }))} disabled={creatingAdmin} />
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
              <button type="button" className="btn-outline" onClick={() => setCreateAdminModalOpen(false)} disabled={creatingAdmin}>Annulla</button>
              <button type="button" className="btn-primary" onClick={() => void onCreateAdmin()} disabled={creatingAdmin}>
                {creatingAdmin ? 'Creazione...' : 'Crea admin'}
              </button>
            </div>
          </div>
        </div>
      )}

      {createOrgModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl border border-border">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <h3 className="text-lg font-semibold text-text-primary">Crea nuova organizzazione</h3>
              <button type="button" className="btn-outline text-xs" onClick={() => setCreateOrgModalOpen(false)} disabled={creatingOrg}>Chiudi</button>
            </div>
            <div className="space-y-3 px-6 py-5">
              <input
                className="w-full rounded-xl border border-border bg-white px-3 py-2"
                placeholder="Nome organizzazione *"
                value={newOrganizationForm.name}
                onChange={(e) => setNewOrganizationForm((prev) => ({ ...prev, name: e.target.value }))}
                disabled={creatingOrg}
              />
              <input
                type="email"
                className="w-full rounded-xl border border-border bg-white px-3 py-2"
                placeholder="Email organizzazione"
                value={newOrganizationForm.email}
                onChange={(e) => setNewOrganizationForm((prev) => ({ ...prev, email: e.target.value }))}
                disabled={creatingOrg}
              />
              <input
                className="w-full rounded-xl border border-border bg-white px-3 py-2"
                placeholder="Sito web (https://...)"
                value={newOrganizationForm.website}
                onChange={(e) => setNewOrganizationForm((prev) => ({ ...prev, website: e.target.value }))}
                disabled={creatingOrg}
              />
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
              <button type="button" className="btn-outline" onClick={() => setCreateOrgModalOpen(false)} disabled={creatingOrg}>Annulla</button>
              <button type="button" className="btn-primary" onClick={() => void onCreateOrganization()} disabled={creatingOrg}>
                {creatingOrg ? 'Creazione...' : 'Crea organizzazione'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileOrganization;
